import { BufferGeometry, CatmullRomCurve3, Group, Line, LineBasicMaterial, Vector3 } from "three"
import { VERTICES_PER_TILE } from "../../consts"
import type { TerrainMesh1EstuaryTilePositionEdge } from "./createTerrainMesh1EstuaryEffect"

const SHOW_DEBUG_MARKERS = false

const halfSize = VERTICES_PER_TILE / 2

const fromRh = 0.00001 * (VERTICES_PER_TILE / 32)
// const riverSideEstuaryBuffer = VERTICES_PER_TILE / 3
const riverRadius0 = VERTICES_PER_TILE / 2.9
const riverOffset0 = VERTICES_PER_TILE / 4
const estuaryPathsCount = 10

export default function appendTerrainMesh1EstuaryParticleCurves(
  edges: Array<TerrainMesh1EstuaryTilePositionEdge>,
  particleCurves: Array<CatmullRomCurve3>,
  particleBoundingBoxPoints: Array<Vector3>,
  group: Group,
): void {
  // console.time('appendTerrainMesh1EstuaryParticleCurves')
  for (let i = 0; i < edges.length; i++) {
    const edge = edges[i]
    const { from, to } = edge

    const worldFromX = from.x * (VERTICES_PER_TILE)
    const worldFromY = from.y * (VERTICES_PER_TILE)
    const worldToX = to.x * (VERTICES_PER_TILE)
    const worldToY = to.y * (VERTICES_PER_TILE)
    const toRh = fromRh

    // Determine if we're moving horizontally or vertically
    const isHorizontal = from.x !== to.x
    const isNorth = from.y > to.y
    const isEast = from.x < to.x

    // from: river
    const riverCorner0 = new Vector3(
      isHorizontal ? worldFromX + (isEast ? riverOffset0 : -riverOffset0) : worldFromX - riverRadius0,
      isHorizontal ? -worldFromY + riverRadius0 : -worldFromY + (isNorth ? riverOffset0 : -riverOffset0),
      fromRh
    )
    const riverCorner1 = new Vector3(
      isHorizontal ? worldFromX + (isEast ? riverOffset0 : -riverOffset0) : worldFromX + riverRadius0,
      isHorizontal ? -worldFromY - riverRadius0 : -worldFromY + (isNorth ? riverOffset0 : -riverOffset0),
      fromRh
    )
    const curve0 = new CatmullRomCurve3([riverCorner0, riverCorner1])

    // using halfSize because it's bigger than riverRadius0
    // therefor the estuary will grow as it leaves the river
    // to: sea
    const seaCorner0 = new Vector3(
      isHorizontal ? worldToX + (isEast ? -riverOffset0 : riverOffset0) : worldToX - halfSize,
      isHorizontal ? -worldToY + halfSize : -worldToY + (isNorth ? -riverOffset0 : riverOffset0),
      toRh
    )
    const seaCorner1 = new Vector3(
      isHorizontal ? worldToX + (isEast ? -riverOffset0 : riverOffset0) : worldToX + halfSize,
      isHorizontal ? -worldToY - halfSize : -worldToY + (isNorth ? -riverOffset0 : riverOffset0),
      toRh
    )
    const curve1 = new CatmullRomCurve3([seaCorner0, seaCorner1])

    particleBoundingBoxPoints.push(riverCorner0, riverCorner1, seaCorner0, seaCorner1)

    const points0 = curve0.getSpacedPoints(estuaryPathsCount)
    const points1 = curve1.getSpacedPoints(estuaryPathsCount)
    if (SHOW_DEBUG_MARKERS) {
      const geometry0 = new BufferGeometry().setFromPoints(points0)
      const geometry1 = new BufferGeometry().setFromPoints(points1)

      const material = new LineBasicMaterial({ color: 0xff0000 })

      // Create the final object to add to the scene
      const curveObject0 = new Line(geometry0, material)
      const curveObject1 = new Line(geometry1, material)
      group.add(curveObject0)
      group.add(curveObject1)
    }

    // create parrallel estuary exit paths
    for (let i = 0; i <= estuaryPathsCount; i++) {
      const point0 = points0[i]
      const point1 = points1[i]
      const pointM = point0.clone().lerp(point1, 0.25)
      // tmpPoint.z += 0.5 * (VERTICES_PER_TILE / 32)
      // const point0 = points0[i]
      // const point1 = points1[i]
      const curveT: CatmullRomCurve3 = new CatmullRomCurve3([point0, pointM, point1])
      particleCurves.push(curveT)
      // if (isHorizontal) {
      //   curveT.horz = true
      // }

      if (SHOW_DEBUG_MARKERS) {
        const points = curveT.getSpacedPoints(estuaryPathsCount)
        const geometry = new BufferGeometry().setFromPoints(points)
        const material = new LineBasicMaterial({ color: 0x00ff00 })
        const curveObject = new Line(geometry, material)
        group.add(curveObject)
        // curveObject.visible = false
      }
    }
  }
  // console.timeEnd('appendTerrainMesh1EstuaryParticleCurves')
}
